import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, RefreshCw, Send, TrendingUp, Lightbulb, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCampusAuth } from "@/contexts/CampusAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Minimum sent count per variant before we suggest a winner.
const MIN_SAMPLE = 30;

const PAIN_LABELS = {
  // B2B
  "md-pruefung": "MD-Prüfung",
  "kuerzungen": "Pflegekassen-Kürzungen",
  "personalmangel": "Personalmangel",
  "pflegesatz": "Pflegesatz-Verhandlung",
  // B2C
  "pflegegrad": "Pflegegrad-Streit",
  "demenz": "Demenz-Patient:in",
  "sturz": "Sturz-Akutfall",
  "karriere": "Karriere-Sprung",
};

function pct(rate) {
  return `${(rate * 100).toFixed(1)} %`;
}

function lift(a, b) {
  if (!a) return null;
  return ((b - a) / a) * 100;
}

export default function CampusReminderStats() {
  const { user } = useCampusAuth();
  const [stats, setStats] = useState(null);
  const [drip, setDrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [dripRunning, setDripRunning] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [reminderRes, dripRes] = await Promise.all([
        axios.get(`${API}/admin/leadmagnet/reminders/stats`, { withCredentials: true }),
        axios.get(`${API}/onboarding/admin/stats`, { withCredentials: true }),
      ]);
      setStats(reminderRes.data);
      setDrip(dripRes.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Konnte Statistik nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  const triggerRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/leadmagnet/reminders/run`, {}, { withCredentials: true });
      await load();
      window.alert(`Reminder-Run erledigt – ${res.data?.sent || 0} Mails versendet.`);
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Run fehlgeschlagen.");
    } finally {
      setRunning(false);
    }
  };

  const triggerDripRun = async () => {
    setDripRunning(true);
    try {
      const res = await axios.post(`${API}/onboarding/admin/drip/run`, {}, { withCredentials: true });
      await load();
      window.alert(`Drip-Run erledigt – ${res.data?.sent || 0} Mails versendet.`);
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Drip-Run fehlgeschlagen.");
    } finally {
      setDripRunning(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (!user?.is_admin) {
    return (
      <div className="rounded-3xl border border-cream-300 bg-white p-8 text-sand-700" data-testid="reminder-stats-locked">
        Diese Seite ist nur für Admins sichtbar.
      </div>
    );
  }

  const a = stats?.a;
  const b = stats?.b;
  const sampleReady = a && b && a.sent >= MIN_SAMPLE && b.sent >= MIN_SAMPLE;
  const winner = sampleReady && (a.conversion_rate !== b.conversion_rate)
    ? (a.conversion_rate > b.conversion_rate ? "a" : "b")
    : null;
  const liftPct = sampleReady ? lift(
    winner === "a" ? b.conversion_rate : a.conversion_rate,
    winner === "a" ? a.conversion_rate : b.conversion_rate,
  ) : null;

  return (
    <div className="space-y-8" data-testid="reminder-stats-page">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="h-5 w-5 text-terra" strokeWidth={1.8} />
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay-dark">
              Admin · A/B-Test
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-sand-800 leading-tight">
            Reminder-Mail-Statistik
          </h1>
          <p className="mt-2 text-sand-600 text-[14.5px] max-w-2xl">
            Performance der beiden 24-Stunden-Reminder-Varianten für Premium-Lead-Magnets.
            Conversion = Lead-Mail wurde später Helden-User.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={load}
            disabled={loading}
            variant="outline"
            data-testid="reminder-stats-reload"
            className="rounded-full h-11 px-5 border-clay text-sand-800 hover:bg-cream-100"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Neu laden
          </Button>
          <Button
            onClick={triggerRun}
            disabled={running}
            data-testid="reminder-stats-run"
            className="rounded-full h-11 px-5 bg-terra hover:bg-terra-dark text-white"
          >
            <Send className="h-4 w-4 mr-2" />
            {running ? "Sende …" : "Run jetzt"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-terra/40 bg-terra/10 text-terra-dark p-4" data-testid="reminder-stats-error">
          {error}
        </div>
      )}

      {/* KPI-CARDS */}
      {stats && (
        <div className="grid md:grid-cols-2 gap-5">
          {[
            { key: "a", label: "Cash- / Schmerz-Hebel", color: "bg-brand-darkred", textColor: "text-white", legendColor: "text-brand-rose" },
            { key: "b", label: "Kollegen-Empfehlung", color: "bg-terra", textColor: "text-white", legendColor: "text-cream-100" },
          ].map(({ key, label, color, textColor, legendColor }) => {
            const s = stats[key] || {};
            const isWinner = winner === key;
            return (
              <div
                key={key}
                data-testid={`reminder-stats-variant-${key}`}
                className={`relative rounded-3xl ${color} ${textColor} p-7 shadow-soft`}
              >
                <div className={`text-[10px] uppercase tracking-[0.28em] font-bold ${legendColor} mb-2`}>
                  Variante {key.toUpperCase()}
                </div>
                <div className="font-display text-2xl leading-tight">{label}</div>

                {isWinner && (
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1 rounded-full bg-white text-brand-darkred px-3 h-7 text-[11px] uppercase tracking-[0.18em] font-bold">
                    <TrendingUp className="h-3 w-3" /> Führung
                  </span>
                )}

                <div className="mt-7 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="font-display text-3xl">{s.sent ?? 0}</div>
                    <div className={`mt-1 text-[10px] uppercase tracking-[0.2em] ${legendColor}`}>Gesendet</div>
                  </div>
                  <div>
                    <div className="font-display text-3xl">{s.converted ?? 0}</div>
                    <div className={`mt-1 text-[10px] uppercase tracking-[0.2em] ${legendColor}`}>Registriert</div>
                  </div>
                  <div>
                    <div className="font-display text-3xl">{pct(s.conversion_rate ?? 0)}</div>
                    <div className={`mt-1 text-[10px] uppercase tracking-[0.2em] ${legendColor}`}>Conversion</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* INSIGHT-BOX */}
      {stats && (
        <div className="rounded-3xl border border-cream-300 bg-white p-7" data-testid="reminder-stats-insight">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-terra/15 text-terra shrink-0">
              <Lightbulb className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl text-sand-800 leading-tight">Hinweis</h2>
              {!sampleReady ? (
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  Wir brauchen mindestens <b>{MIN_SAMPLE} versendete Mails pro Variante</b>, bevor eine Aussage seriös wird.
                  Aktuell: A {a?.sent ?? 0} · B {b?.sent ?? 0}.
                  Aktiviere weitere Premium-Funnels und warte ein paar Wochen, dann zeigen wir dir den Gewinner.
                </p>
              ) : winner ? (
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  Variante <b className="text-terra">{winner.toUpperCase()}</b> liegt mit{" "}
                  <b>{pct(winner === "a" ? a.conversion_rate : b.conversion_rate)}</b> Conversion vorne –
                  ein <b>Lift von {liftPct?.toFixed(1)} %</b> gegenüber der schwächeren Variante.
                  Wenn du auf die Gewinner-Variante umstellen willst, kann ich den Worker auf
                  80/20 oder 100/0 umbauen. Sag Bescheid.
                </p>
              ) : (
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  Beide Varianten liegen gleichauf. Etwas mehr Daten könnte den Unterschied noch zeigen –
                  oder beide Texte ziehen gleich gut. Beides ist ein gutes Signal.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* GESAMT */}
      {stats?.total && (
        <div className="rounded-3xl bg-cream-50 border border-cream-300 p-7">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-3">
            Gesamt
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="font-display text-3xl text-sand-800">{stats.total.sent}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-sand-500 mt-1">Mails versendet</div>
            </div>
            <div>
              <div className="font-display text-3xl text-sand-800">{stats.total.converted}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-sand-500 mt-1">Wurden Helden-User</div>
            </div>
            <div>
              <div className="font-display text-3xl text-terra">
                {stats.total.sent ? pct(stats.total.converted / stats.total.sent) : "–"}
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-sand-500 mt-1">Gesamt-Conversion</div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* DRIP-FUNNEL · Onboarding-Welcome → 3-Mail-Sequenz             */}
      {/* ============================================================ */}
      <DripSection drip={drip} onRun={triggerDripRun} running={dripRunning} />
    </div>
  );
}

function DripSection({ drip, onRun, running }) {
  if (!drip) return null;
  const completed = drip.total_completed || 0;
  const skipped = drip.total_skipped || 0;
  const pending = drip.total_pending || 0;
  const dripSent = drip.drip_sent || { 1: 0, 2: 0, 3: 0 };
  const totalAudience = completed + skipped;

  // Funnel-Conversion: Wieviel % der completed-User haben Step n erreicht
  const stepPct = (n) => (completed ? (dripSent[n] / completed) * 100 : 0);

  // Top pain points (sorted desc)
  const breakdown = (drip.breakdown || []).slice().sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6 pt-8 border-t border-cream-300" data-testid="drip-funnel-section">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="h-5 w-5 text-terra" strokeWidth={1.8} />
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay-dark">
              Admin · Onboarding-Drip
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-sand-800 leading-tight">
            Welcome-Funnel & 3-Mail-Sequenz
          </h2>
          <p className="mt-2 text-sand-600 text-[14.5px] max-w-2xl">
            Personalisierte 3-Mail-Drip nach Pain-Point an Tag&nbsp;1 / 4 / 8 nach Abschluss
            der Welcome-Tour.
          </p>
        </div>
        <Button
          onClick={onRun}
          disabled={running}
          data-testid="drip-funnel-run"
          className="rounded-full h-11 px-5 bg-sand-800 hover:bg-sand-700 text-white"
        >
          <Send className="h-4 w-4 mr-2" />
          {running ? "Sende …" : "Drip jetzt"}
        </Button>
      </div>

      {/* Welcome-Tour Status */}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="drip-funnel-tour-status">
        <FunnelStat label="Welcome abgeschlossen" value={completed} accent="text-terra" />
        <FunnelStat label="Übersprungen" value={skipped} accent="text-sand-700" />
        <FunnelStat label="Noch offen" value={pending} accent="text-sand-500" />
      </div>

      {/* 4-Step Funnel: Completed → Step 1 → 2 → 3 */}
      <div className="rounded-3xl border border-cream-300 bg-white p-7" data-testid="drip-funnel-steps">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-5">
          Funnel-Lauf · {totalAudience} Welcome-Tour-Teilnehmer:innen insgesamt
        </div>
        <div className="space-y-4">
          <FunnelBar
            label="Welcome abgeschlossen"
            value={completed}
            pctOfTotal={100}
            base={completed}
            highlight
          />
          <FunnelBar
            label="Drip Step 1 versendet (Tag 1)"
            value={dripSent[1] || 0}
            pctOfTotal={stepPct(1)}
            base={completed}
          />
          <FunnelBar
            label="Drip Step 2 versendet (Tag 4)"
            value={dripSent[2] || 0}
            pctOfTotal={stepPct(2)}
            base={completed}
          />
          <FunnelBar
            label="Drip Step 3 versendet (Tag 8)"
            value={dripSent[3] || 0}
            pctOfTotal={stepPct(3)}
            base={completed}
          />
        </div>
      </div>

      {/* Top Pain Points */}
      {breakdown.length > 0 && (
        <div className="rounded-3xl border border-cream-300 bg-cream-50 p-7" data-testid="drip-funnel-breakdown">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-4">
            Top Pain-Points · Welcome-Tour-Antworten
          </div>
          <ul className="space-y-2">
            {breakdown.slice(0, 8).map((b, i) => {
              const pctOfCompleted = completed ? (b.count / completed) * 100 : 0;
              return (
                <li
                  key={`${b.audience}-${b.pain_point}`}
                  className="flex items-center justify-between gap-3 py-2 border-b border-cream-300 last:border-b-0"
                  data-testid={`drip-pain-${b.pain_point}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-[11px] font-semibold text-sand-500 w-6">#{i + 1}</span>
                    <span className={`inline-flex items-center rounded-full px-2 h-5 text-[10px] uppercase tracking-[0.18em] font-bold ${b.audience === "b2b" ? "bg-brand-darkred/15 text-brand-darkred" : "bg-terra/15 text-terra"}`}>
                      {b.audience}
                    </span>
                    <span className="text-[14px] text-sand-800 font-medium truncate">
                      {PAIN_LABELS[b.pain_point] || b.pain_point}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-display text-lg text-sand-800">{b.count}</span>
                    <span className="text-[11px] text-sand-500 tabular-nums w-12 text-right">
                      {pctOfCompleted.toFixed(0)}&nbsp;%
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function FunnelStat({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-cream-300 bg-white p-5">
      <div className={`font-display text-3xl ${accent}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-sand-500 mt-1">{label}</div>
    </div>
  );
}

function FunnelBar({ label, value, pctOfTotal, base, highlight }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className={`text-[13.5px] ${highlight ? "font-semibold text-sand-800" : "text-sand-700"}`}>
          {label}
        </span>
        <span className="font-display text-[1.05rem] tabular-nums text-sand-800">
          {value} <span className="text-[12px] text-sand-500">/ {base || 0}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-cream-200 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${highlight ? "bg-terra" : "bg-sand-700"}`}
          style={{ width: `${Math.min(100, pctOfTotal)}%` }}
        />
      </div>
    </div>
  );
}
